/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import * as React from 'react';

import type { Contract } from '@coursera-web/enterprise-admin-types';

import { MOMENT_FORMATS, formatDateTimeDisplay } from 'js/utils/DateTimeUtils';

import { Button, LoadingSection, Search, TableEmptyView, Typography2 } from '@coursera/cds-core';

import { getFormattedContractType } from 'bundles/enterprise-admin-users/utils';

import _t from 'i18n!nls/enterprise-ui';

import RadioTable from './RadioTable';
import RemainingUtilization from './RemainingUtilization';

const DateCell: React.FC<{ children?: string | null }> = ({ children }) => (
  <Typography2
    component="span"
    css={css`
      white-space: nowrap;
    `}
  >
    {children ? formatDateTimeDisplay(children, MOMENT_FORMATS.MED_DATE_ONLY_DISPLAY) : _t('N/A')}
  </Typography2>
);

type Props = {
  rows?: Contract[];
  value?: string;
  onChange: (id: string) => void;
  query: string;
  onQueryChange: (q: string) => void;
  onResetSearch?: () => void;
  maxHeight?: string;
};

const ContractSelector = ({
  rows,
  value,
  onChange,
  query,
  onQueryChange,
  onResetSearch,
  maxHeight = '400px',
}: Props) => {
  const filtered = React.useMemo(() => {
    if (!rows) return undefined;
    const q = query.trim().toLowerCase();
    if (!q) return rows;
    return rows.filter((c) => c.name.toLowerCase().includes(q));
  }, [rows, query]);

  const columns = React.useMemo(
    () => [
      { accessor: 'name', label: _t('Name'), width: '160px' },
      {
        accessor: 'limitType',
        label: _t('Type'),
        renderCell: (contract: Contract) => (
          <Typography2 component="span">{getFormattedContractType(contract.limitType)}</Typography2>
        ),
      },
      {
        accessor: 'remaining',
        label: _t('Remaining'),
        renderCell: (contract: Contract) => (
          <Typography2 component="span">
            <RemainingUtilization contract={contract} />
          </Typography2>
        ),
      },
      {
        accessor: 'startsAt',
        label: _t('Start date'),
        renderCell: (contract: Contract) => <DateCell>{contract.startsAt}</DateCell>,
      },
      {
        accessor: 'endsAt',
        label: _t('End date'),
        renderCell: (contract: Contract) => <DateCell>{contract.endsAt}</DateCell>,
      },
    ],
    []
  );

  return (
    <section>
      <div
        css={css`
          margin-bottom: var(--cds-spacing-300);
        `}
      >
        <Search
          fullWidth
          hideSearchIcon
          value={query}
          onChange={onQueryChange}
          placeholder={_t('Search for a contract by name')}
        />
      </div>
      {filtered ? (
        <RadioTable
          aria-label={_t('Available contracts')}
          columns={columns}
          rows={filtered}
          maxHeight={maxHeight}
          value={value}
          onChange={onChange}
          emptyState={
            <TableEmptyView
              actions={
                query && onResetSearch ? <Button onClick={onResetSearch}>{_t('Reset search')}</Button> : undefined
              }
              label={query ? _t('No results found') : _t('No contracts available')}
            />
          }
        />
      ) : (
        <LoadingSection
          css={css`
            padding: var(--cds-spacing-600) 0;
          `}
        />
      )}
    </section>
  );
};

export default ContractSelector;
